import axios from 'axios'
import LocalDatabase from '../utils/LocalDatabase'
import Types from '../store/types'

const gcemain = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    /*'Access-Control-Allow-Origin':'*',*/
    Accept: 'application/json'
  }
})

// Token refresh management for GCE
let isRefreshingGCE = false
let failedQueueGCE = []

const processQueueGCE = (error, token = null) => {
  failedQueueGCE.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueueGCE = []
}

// Response interceptor for GCE token refresh
gcemain.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config

    // Handle network errors or missing response
    if (!error.response) {
      return Promise.reject(error)
    }

    const { status } = error.response

    // Only handle 401 errors
    if (status !== 401) {
      return Promise.reject(error)
    }

    // If refresh token endpoint itself failed, logout user
    if (originalRequest.url && originalRequest.url.includes('/gce/refresh-token')) {
      console.log('GCE token refresh failed, redirecting to login')
      window.location.href = '/gce/login'
      return Promise.reject(error)
    }

    // Prevent infinite retry loops
    if (originalRequest._retry) {
      window.location.href = '/gce/login'
      return Promise.reject(error)
    }

    // If already refreshing, queue this request
    if (isRefreshingGCE) {
      return new Promise(function(resolve, reject) {
        failedQueueGCE.push({ resolve, reject })
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return gcemain(originalRequest)
        })
        .catch(err => {
          return Promise.reject(err)
        })
    }

    // Mark request as retried
    originalRequest._retry = true
    isRefreshingGCE = true

    // Attempt to refresh the GCE token
    try {
      const token = await LocalDatabase.get(Types.localdb.GCE_USER_TOKEN)

      if (!token) {
        throw new Error('No GCE token found')
      }

      console.log('GCE token expired, refreshing...')

      // Call GCE refresh-token endpoint
      const response = await gcemain.post('/gce/refresh-token', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data && response.data.access_token) {
        const newToken = response.data.access_token

        // Save new GCE token
        await LocalDatabase.set(Types.localdb.GCE_USER_TOKEN, newToken)

        // Update axios default headers
        gcemain.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        console.log('GCE token refreshed successfully')

        // Process queued requests
        processQueueGCE(null, newToken)

        // Retry original request with new token
        return gcemain(originalRequest)
      } else {
        throw new Error('No access_token in GCE refresh response')
      }
    } catch (refreshError) {
      console.error('GCE token refresh error:', refreshError)
      processQueueGCE(refreshError, null)

      // Redirect to GCE login on refresh failure
      window.location.href = '/gce/login'

      return Promise.reject(refreshError)
    } finally {
      isRefreshingGCE = false
    }
  }
)

// Add request interceptor to attach GCE token dynamically
gcemain.interceptors.request.use(
  async (config) => {
    const gceToken = await LocalDatabase.get(Types.localdb.GCE_USER_TOKEN)
    if (gceToken) {
      config.headers.Authorization = `Bearer ${gceToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export default gcemain
